import axios from 'axios';
import { nextTick } from 'vue';
import { calculateMatchScore } from '@/services/recommendationService';
import { compareMarketRelevance } from '@/utils/citySearchPriority';
import { BOOKING_INTEGRATION_ENABLED } from '@/config/bookingIntegration';
import {
  partitionScrapedBlock,
  finalizeScrapedBlock,
  isMarketplaceHiddenProvider,
} from '../../../shared/providerSearchBoost.cjs';
import {
  isCanaryIslandsLocationLabel,
  shouldIncludeNativeColivingInSearch,
} from '../../../shared/nativeColivingSearchVisibility.cjs';
import {
  parseListPageFromQuery,
  clampListPage,
  itemsNeededForListPage,
  SEARCH_LIST_ITEMS_PER_PAGE,
} from '@/utils/searchListPagination';
import { isPrerenderCapture } from '@/utils/prerenderCapture';
import {
  readColivingHubListCache,
  writeColivingHubListCache,
  isHubBaselineListQuery,
} from '@/utils/colivingHubListCache';
import {
  readColivingProviderListCache,
  writeColivingProviderListCache,
  isProviderBaselineListQuery,
} from '@/utils/colivingProviderListCache';
import { tryFetchHubListSnapshot } from '@/services/colivingHubListApi';
import { tryFetchProviderListSnapshot } from '@/services/colivingProviderListApi';
import { getSearchPropertiesWithRetry } from '@/utils/apiBaseUrl';
import { groupBluegroundListingsForSearch } from '@/utils/bluegroundSearchGrouping';
import { groupSpacestListingsForSearch } from '@/utils/spacestSearchGrouping';

/** Primera respuesta API = cards visibles en lista (12) */
const INITIAL_PAGE_SIZE = SEARCH_LIST_ITEMS_PER_PAGE;
/** Carga completa solo cuando el usuario abre el mapa */
const MAP_PAGE_SIZE = 500;
/** Máximo por petición al ampliar lista sin mapa */
const LIST_FETCH_MAX = 120;

/**
 * API search, pagination, sorting and map background loading.
 */
export function useColivingSearchEngine(deps) {
  let mapFullDataLoaded = false;
  let activeListSearchAbort = null;
  let listSearchGeneration = 0;

  const {
    route,
    router,
    locale,
    regionId,
    filters,
    selectedLocations,
    currentUser,
    currentCity,
    searchResults,
    searchPerformed,
    searchError,
    isLoading,
    isLoadingMore,
    isLoadingMapResults,
    mapLoadVersion,
    currentPage,
    listPage,
    hasMorePages,
    totalResults,
    isMobileMapViewActive,
    showDesktopMap,
    mapSearchResults,
    isInitializing,
    userPreferences,
    showToast,
    trackSearch,
    trackZeroResults,
    trackLoadMore,
    trackSearchNoClick,
    trackFirstSearchDelay,
    _firstSearchDone,
    _lastSearchHadClick,
    _lastSearchCity,
    _pageLoadTs,
  } = deps;

  function buildSearchParams(pageSizeOverride, options = {}) {
  const queryParams = {
    contractType: 'coliving',
    includeMarketStats: 'false',
    pageSize: pageSizeOverride || INITIAL_PAGE_SIZE,
    page: currentPage.value,
    region: regionId.value, // Punto 1: región para analytics
    lang: (locale.value || 'es').slice(0, 2), // Punto 7: idioma de la sesión
    searchPath: route.path, // Opción B: URL de la página (independiente del filtro de ciudad)
  };

  if (options.responseMode) {
    queryParams.responseMode = options.responseMode;
  }

  const hubFast = options.hubFast === true
    && !route.params.city
    && !route.params.provider
    && !route.params.priceSlug;

  if (filters.value.sortBy) {
    queryParams.sortBy = filters.value.sortBy;
  }

  // Ubicación (omitida en hubFast: 1ª carga del hub nacional usa snapshot/caché)
  if (!hubFast) {
    if (selectedLocations.value.length === 1) {
      queryParams.propertyLocation = selectedLocations.value[0];
    } else if (selectedLocations.value.length > 1) {
      queryParams.propertyLocations = selectedLocations.value.join(',');
    }
  }

  // Filtros de resultados — siempre al API (hubFast no debe omitir precio ni otros filtros)
  if (filters.value.maxPrice !== null && filters.value.maxPrice !== 0) {
    if (filters.value.maxPrice > 0) {
      queryParams.maxRoomPrice = filters.value.maxPrice;
    } else {
      queryParams.minRoomPrice = Math.abs(filters.value.maxPrice);
    }
  }
  if (filters.value.policies.pets) queryParams.allowsPets = 'yes';
  if (filters.value.policies.couples) queryParams.allowsCouples = 'yes';
  if (filters.value.maxMinimumStay !== null) {
    queryParams.maxMinimumStay = filters.value.maxMinimumStay;
  }
  if (filters.value.services.internet) queryParams.servicesInternet = 'yes';
  if (filters.value.services.cleaning) queryParams.servicesCleaning = 'yes';
  if (filters.value.services.allIncluded) queryParams.servicesAllIncluded = 'yes';
  if (filters.value.minBathrooms !== null) {
    queryParams.minBathrooms = filters.value.minBathrooms;
  }
  if (filters.value.accommodationType.building && !filters.value.accommodationType.apartment) {
    queryParams.accommodationType = 'building';
  } else if (filters.value.accommodationType.apartment && !filters.value.accommodationType.building) {
    queryParams.accommodationType = 'apartment';
  }
  if (filters.value.providers.length > 0) {
    queryParams.providers = filters.value.providers.join(',');
  }
  if (filters.value.forStudents) queryParams.forStudents = 'yes';
  if (filters.value.proOnly) queryParams.proOnly = 'yes';

  // No enviar userId: evita cache SKIP + merge completo (~15–20s) en searchProperties.
  // Guardados / favoritos vienen de setupSavedColivingsSubscription (ContractCard isSaved).

    return queryParams;
  }

  function listingPriceForMaxFilter(item) {
    const priceFrom = Number(item.priceFrom);
    const propertyPrice = Number(item.propertyPrice);
    if (Number.isFinite(priceFrom) && priceFrom > 0) return priceFrom;
    if (Number.isFinite(propertyPrice) && propertyPrice > 0) return propertyPrice;
    return null;
  }

  function isCanaryIslandsSearchActive() {
    if (currentCity?.value && isCanaryIslandsLocationLabel(currentCity.value)) {
      return true;
    }
    const locs = selectedLocations?.value || [];
    if (locs.length === 0) return false;
    return locs.some((loc) => isCanaryIslandsLocationLabel(loc));
  }

  function passesPriceFilter(item, priceValue) {
    if (priceValue == null || priceValue === 0) return true;

    const isMinFilter = priceValue < 0;
    const threshold = Math.abs(priceValue);

    // Blueground no publica precios; asumir >1200€ para filtrado
    const isBlueground = item.externalSource === 'blueground' || item.source === 'blueground';
    if (isBlueground) {
      const listPrice = listingPriceForMaxFilter(item);
      const effectivePrice = (listPrice != null && listPrice > 0) ? listPrice : 1201;
      return isMinFilter ? effectivePrice >= threshold : effectivePrice <= threshold;
    }

    const rooms = item.rooms || item.roomsConfiguration;
    if (Array.isArray(rooms) && rooms.length > 0) {
      const available = rooms.filter((room) =>
        room.status === 'libre' || room.status === 'available'
        || !room.status
        || ((room.status === 'ocupada' || room.status === 'occupied') && !room.currentTenant),
      );
      const pool = available.length > 0 ? available : rooms;
      if (isMinFilter) {
        if (pool.some((room) => Number(room.price) >= threshold)) return true;
      } else {
        if (pool.some((room) => Number(room.price) > 0 && Number(room.price) <= threshold)) return true;
      }
    }
    const listPrice = listingPriceForMaxFilter(item);
    if (isMinFilter) return listPrice == null || listPrice >= threshold;
    return listPrice == null || listPrice <= threshold;
  }

  // Helper: Procesar resultados de la API (filtrar, calcular match, etc.)
  function processApiResults(results) {
  const maxPrice = filters.value.maxPrice;
  const canarySearch = isCanaryIslandsSearchActive();
  // Filtrar solo colivings disponibles
  let processed = results.filter((contract) =>
    contract.contractType === 'coliving'
    && !contract.assignedApplicant
    && !(contract.isExternal && isMarketplaceHiddenProvider(contract))
    && shouldIncludeNativeColivingInSearch(
      contract.id,
      canarySearch,
      contract.isNative === true || (!contract.isExternal && contract.isNative !== false),
    )
    && passesPriceFilter(contract, maxPrice),
  );

  // Calcular match score si hay preferencias del usuario
  if (userPreferences.value) {
    processed = processed.map(coliving => ({
      ...coliving,
      matchData: calculateMatchScore(userPreferences.value, coliving)
    }));
  }

    processed = groupSpacestListingsForSearch(processed);
    processed = groupBluegroundListingsForSearch(processed);

    return processed;
  }

  function isRouteOnlyProviderFilter() {
    const slug = route.params.provider;
    if (!slug) return false;
    if (filters.value.providers?.length !== 1) return false;
    return filters.value.providers[0] === slug;
  }

  // Búsqueda diferida — compatibilidad con llamadas externas
  function deferredSearch() {
    nextTick(() => {
      if (!searchPerformed.value) {
        performSearch();
      }
    });
  }

  // Método de búsqueda principal (FASE 1: carga rápida de resultados visibles)
// isAutoSearch=true en la primera carga automática (no cuenta como acción del usuario)
  async function performSearch(resetPage = true, isAutoSearch = false) {
  if (window.innerWidth < 1024) {
    isMobileMapViewActive.value = false;
  }

  const hasPriceOrFilterInUrl = Boolean(
    route.params.priceSlug
    || (route.query.maxPrice && Number(route.query.maxPrice) !== 0),
  );
  const hasActiveListFilters = Boolean(
    (filters.value.maxPrice != null && filters.value.maxPrice !== 0)
    || filters.value.maxMinimumStay != null
    || filters.value.policies.pets
    || filters.value.policies.couples
    || filters.value.forStudents
    || filters.value.proOnly
    || (filters.value.providers?.length > 0 && !isRouteOnlyProviderFilter())
    || filters.value.services.internet
    || filters.value.services.cleaning
    || filters.value.services.allIncluded
    || filters.value.minBathrooms != null
    || filters.value.accommodationType.building
    || filters.value.accommodationType.apartment,
  );

  const allowHubFast = isAutoSearch
    && !route.meta?.isStudentMode
    && !route.path.startsWith('/residencias')
    && !hasPriceOrFilterInUrl
    && !hasActiveListFilters
    && !route.params.provider;

  const allowProviderFast = isAutoSearch
    && isRouteOnlyProviderFilter()
    && !route.meta?.isStudentMode
    && !route.path.startsWith('/residencias')
    && !hasPriceOrFilterInUrl
    && !hasActiveListFilters;

  // Si es una nueva búsqueda, resetear página y resultados
  if (resetPage) {
    currentPage.value = 1;
    // En el montaje inicial preservamos ?page=N hasta applyInitialUrlPage()
    if (!isInitializing.value) {
      resetListPage();
    }
    mapLoadVersion.value++;
    isLoadingMapResults.value = false;
    mapFullDataLoaded = false;
    if (mapSearchResults) mapSearchResults.value = [];

    const previewParams = buildSearchParams(INITIAL_PAGE_SIZE, { hubFast: allowHubFast });
    const hubBaseline = isHubBaselineListQuery(
      previewParams,
      allowHubFast ? [] : selectedLocations.value,
      allowHubFast ? null : (currentCity?.value ?? currentCity),
    );
    const providerBaselinePreview = isProviderBaselineListQuery(
      previewParams,
      route.params.provider,
      allowProviderFast ? [] : selectedLocations.value,
      allowProviderFast ? null : (currentCity?.value ?? currentCity),
    );
    const providerCache = providerBaselinePreview && route.params.provider
      ? readColivingProviderListCache(
        route.params.provider,
        regionId.value,
        filters.value.sortBy,
      )
      : null;

    const hubCache = hubBaseline
      ? readColivingHubListCache(regionId.value, filters.value.sortBy)
      : null;

    const instantCache = providerCache?.results?.length ? providerCache : hubCache;

    if (instantCache?.results?.length) {
      searchResults.value = processApiResults(instantCache.results);
      totalResults.value = instantCache.totalResults ?? searchResults.value.length;
      searchPerformed.value = true;
      isLoading.value = false;
    } else {
      searchResults.value = [];
      isLoading.value = true;
    }

    listSearchGeneration += 1;
    if (activeListSearchAbort) activeListSearchAbort.abort();
    activeListSearchAbort = new AbortController();
  } else {
    isLoadingMore.value = true;
  }

  searchError.value = '';
  searchPerformed.value = true;

  const myListGeneration = listSearchGeneration;

  try {
    // FASE 1: Pedir solo INITIAL_PAGE_SIZE resultados para mostrar la lista rápido
    const requestedPageSize = resetPage ? INITIAL_PAGE_SIZE : LIST_FETCH_MAX;
    const queryParams = buildSearchParams(requestedPageSize, { hubFast: allowHubFast });
    const hubBaseline = isHubBaselineListQuery(
      queryParams,
      allowHubFast ? [] : selectedLocations.value,
      allowHubFast ? null : (currentCity?.value ?? currentCity),
    );
    const providerBaseline = isProviderBaselineListQuery(
      queryParams,
      route.params.provider,
      allowProviderFast ? [] : selectedLocations.value,
      allowProviderFast ? null : (currentCity?.value ?? currentCity),
    );
    const hadHubPreview = resetPage && searchResults.value.length > 0 && !isLoading.value;

    const apiUrl = process.env.VUE_APP_SEARCH_API_URL;
    if (!apiUrl) throw new Error("VUE_APP_SEARCH_API_URL is not defined");

    if (resetPage && !hadHubPreview) {
      isLoading.value = true;
    }

    const axiosConfig = {
      params: queryParams,
      signal: resetPage ? activeListSearchAbort?.signal : undefined,
    };

    let response = null;
    if (resetPage && allowProviderFast && providerBaseline && route.params.provider) {
      const searchPromise = getSearchPropertiesWithRetry(axios, apiUrl, axiosConfig);
      const providerPromise = tryFetchProviderListSnapshot({
        provider: route.params.provider,
        region: queryParams.region,
        pageSize: queryParams.pageSize,
        page: queryParams.page,
        sortBy: queryParams.sortBy || 'market_relevance',
        signal: activeListSearchAbort?.signal,
      });
      const providerSnap = await providerPromise;
      response = providerSnap?.results
        ? { data: providerSnap }
        : await searchPromise;
    } else if (resetPage && allowHubFast && hubBaseline) {
      const searchPromise = getSearchPropertiesWithRetry(axios, apiUrl, axiosConfig);

      const hubPromise = tryFetchHubListSnapshot({
        region: queryParams.region,
        pageSize: queryParams.pageSize,
        page: queryParams.page,
        sortBy: queryParams.sortBy || 'market_relevance',
        signal: activeListSearchAbort?.signal,
      });

      const hubSnap = await hubPromise;
      if (hubSnap?.results) {
        response = { data: hubSnap };
      } else {
        response = await searchPromise;
      }
    } else {
      response = await getSearchPropertiesWithRetry(axios, apiUrl, axiosConfig);
    }

    if (myListGeneration !== listSearchGeneration) return;

    if (response.data && Array.isArray(response.data.results)) {
      const results = processApiResults(response.data.results);

      if (resetPage) {
        searchResults.value = results;
      } else {
        searchResults.value = [...searchResults.value, ...results];
      }

      if (response.data.totalResults !== undefined) {
        totalResults.value = response.data.totalResults;
      }

      if (resetPage && hubBaseline && response.data.results) {
        writeColivingHubListCache(
          regionId.value,
          filters.value.sortBy,
          response.data.results,
          response.data.totalResults,
        );
      }
      if (resetPage && providerBaseline && route.params.provider && response.data.results) {
        writeColivingProviderListCache(
          route.params.provider,
          regionId.value,
          filters.value.sortBy,
          response.data.results,
          response.data.totalResults,
        );
      }

      if (resetPage) {
        // Ciudad real: preferir selección del dropdown; fallback a slug de URL
        const ciudadTracking = selectedLocations.value.length > 0
          ? selectedLocations.value[0]
          : (currentCity.value || '');
        const totalTracking = response.data.totalResults || 0;
        const filtrosTracking = {
          maxPrice: filters.value.maxPrice || null,
          minPrice: filters.value.minPrice || null,
          rooms: filters.value.rooms || null,
          petsAllowed: filters.value.petsAllowed || null,
          couplesAllowed: filters.value.couplesAllowed || null,
          forStudents: filters.value.forStudents || null,
          minStayMonths: filters.value.minStayMonths || null,
          contractType: filters.value.contractType || null,
          sortBy: filters.value.sortBy || null,
        };

        // Punto 3: si la búsqueda anterior no tuvo clicks, registrarlo con la ciudad ANTERIOR
        if (searchPerformed.value && !_lastSearchHadClick.value) {
          trackSearchNoClick({
            ciudad: _lastSearchCity.value, // ciudad de la búsqueda sin click, no la nueva
            region: regionId.value || 'es',
            totalResultados: totalTracking,
          });
        }
        _lastSearchHadClick.value = false; // resetear para la nueva búsqueda
        _lastSearchCity.value = ciudadTracking; // guardar ciudad de esta búsqueda

        // Punto 6: tiempo hasta primera búsqueda MANUAL (excluye la carga automática inicial)
        if (!_firstSearchDone.value && !isAutoSearch) {
          _firstSearchDone.value = true;
          const delaySeconds = Math.round((Date.now() - _pageLoadTs) / 1000);
          trackFirstSearchDelay({ delaySeconds });
        }

        if (totalTracking === 0) {
          trackZeroResults({ ciudad: ciudadTracking, filtros: filtrosTracking });
        } else {
          trackSearch({
            ciudad: ciudadTracking,
            totalResultados: totalTracking,
            isLoggedIn: !!currentUser.value,
            filtros: filtrosTracking,
          });
        }
      }

      const currentlyLoaded = searchResults.value.length;
      hasMorePages.value = currentlyLoaded < totalResults.value;

      sortSearchResults();

      if (resetPage && !isPrerenderCapture()) {
        const mapUiActive = showDesktopMap?.value || isMobileMapViewActive.value;
        if (mapUiActive && hasMorePages.value) {
          requestMapResults();
        }
      }
    } else {
      if (resetPage) {
        searchResults.value = [];
      }
      hasMorePages.value = false;
    }

  } catch (error) {
    if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
      return;
    }
    if (myListGeneration !== listSearchGeneration) return;
    searchError.value = 'Error al buscar colivings. Por favor, intenta de nuevo.';
    if (error.response?.data?.includes('index')) {
      searchError.value = 'Error de configuración en la búsqueda.';
    } else if (error.request) {
      searchError.value = 'Cargando los mejores colivings de España. Miles de habitaciones verificadas desde 350€/mes en Madrid, Barcelona, Valencia y más. Por favor, espera un momento...';
    } else if (error.message.includes("VUE_APP_SEARCH_API_URL")) {
      searchError.value = 'Error de configuración del sistema.';
    }
    showToast(searchError.value, 'error');
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }
}

  function requestMapResults(options = {}) {
    if (isPrerenderCapture()) return;
    if (options.force) {
      mapFullDataLoaded = false;
      if (mapSearchResults) mapSearchResults.value = [];
    }
    if (mapFullDataLoaded || isLoadingMapResults.value) return;
    if (!searchPerformed.value || totalResults.value === 0) return;
    loadMapResults();
  }

  async function fetchListResultsUpTo(minCount) {
    const cap = totalResults.value > 0
      ? Math.min(minCount, totalResults.value)
      : minCount;
    const targetSize = Math.min(
      cap,
      totalResults.value > 0 ? totalResults.value : cap,
      LIST_FETCH_MAX,
    );
    if (searchResults.value.length >= targetSize) return;

    const apiUrl = process.env.VUE_APP_SEARCH_API_URL;
    if (!apiUrl) return;

    isLoadingMore.value = true;
    try {
      while (searchResults.value.length < targetSize) {
        const nextApiPage = Math.floor(searchResults.value.length / INITIAL_PAGE_SIZE) + 1;
        const queryParams = buildSearchParams(INITIAL_PAGE_SIZE);
        queryParams.page = nextApiPage;

        const response = await getSearchPropertiesWithRetry(axios, apiUrl, {
          params: queryParams,
          // Sin cache-busting headers: la Cloud Function ya gestiona la caché en memoria.
        });

        if (!response.data || !Array.isArray(response.data.results)) break;

        const batch = processApiResults(response.data.results);
        if (batch.length === 0) break;

        if (response.data.totalResults !== undefined) {
          totalResults.value = response.data.totalResults;
        }

        const seen = new Set(searchResults.value.map((r) => r.id));
        const merged = [...searchResults.value];
        for (const item of batch) {
          if (item?.id && !seen.has(item.id)) {
            seen.add(item.id);
            merged.push(item);
          }
        }
        searchResults.value = merged;
        hasMorePages.value = searchResults.value.length < totalResults.value;

        if (batch.length < INITIAL_PAGE_SIZE) break;
      }
    } catch (error) {
      console.warn('Error ampliando resultados de lista:', error);
    } finally {
      isLoadingMore.value = false;
    }
  }

// Cargar resultados completos para el mapa (solo cuando el usuario lo abre)
  async function loadMapResults() {
  if (isLoadingMapResults.value) return;
  isLoadingMapResults.value = true;

  // Capturar la versión actual en el momento de arrancar.
  // Si performSearch(true) incrementa mapLoadVersion mientras estamos en vuelo,
  // sabremos que somos "stale" y no debemos sobrescribir searchResults.
  const myVersion = mapLoadVersion.value;

  try {
    const queryParams = buildSearchParams(MAP_PAGE_SIZE, { responseMode: 'map' });
    queryParams.page = 1; // Siempre página 1 para obtener todos

    const apiUrl = process.env.VUE_APP_SEARCH_API_URL;
    if (!apiUrl) return;

    const response = await getSearchPropertiesWithRetry(axios, apiUrl, { params: queryParams });

    // Verificar que seguimos siendo la búsqueda activa (no stale por una nueva búsqueda)
    if (myVersion !== mapLoadVersion.value) {
      return;
    }

    if (response.data && Array.isArray(response.data.results)) {
      const results = processApiResults(response.data.results);

      if (mapSearchResults) {
        mapSearchResults.value = results;
      } else {
        searchResults.value = results;
      }

      if (response.data.totalResults !== undefined) {
        totalResults.value = response.data.totalResults;
      }

      const listLoaded = searchResults.value.length;
      hasMorePages.value = listLoaded < totalResults.value;
      mapFullDataLoaded = true;
    }
  } catch (error) {
    // Error silencioso: la lista ya tiene resultados, solo falla la carga del mapa
    console.warn('Error cargando resultados adicionales para el mapa:', error);
  } finally {
    // Solo liberar el flag si todavía somos la carga activa
    if (myVersion === mapLoadVersion.value) {
      isLoadingMapResults.value = false;
    }
  }
}

function syncPageToQuery(page) {
  const query = { ...route.query };
  if (page <= 1) {
    if (!query.page) return;
    delete query.page;
  } else if (query.page === String(page)) {
    return;
  } else {
    query.page = String(page);
  }
  router.replace({ path: route.path, query, hash: route.hash });
}

function resetListPage() {
  listPage.value = 1;
  syncPageToQuery(1);
}

async function waitForMapLoad() {
  let attempts = 0;
  while (isLoadingMapResults.value && attempts < 300) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    attempts++;
  }
}

async function ensureDataForListPage(page) {
  const itemsNeeded = itemsNeededForListPage(page);
  if (searchResults.value.length >= itemsNeeded) return;

  if (mapFullDataLoaded && searchResults.value.length >= itemsNeeded) return;

  if (isLoadingMapResults.value) {
    await waitForMapLoad();
    if (searchResults.value.length >= itemsNeeded) return;
  }

  await fetchListResultsUpTo(itemsNeeded);
}

async function onListPageChange(page) {
  const clamped = totalResults.value > 0
    ? clampListPage(page, totalResults.value)
    : Math.max(1, page);
  listPage.value = clamped;
  syncPageToQuery(clamped);
  await ensureDataForListPage(clamped);
}

async function applyInitialUrlPage() {
  const target = parseListPageFromQuery(route.query.page);
  if (target <= 1) return;
  await ensureDataForListPage(target);
  const clamped = clampListPage(target, totalResults.value);
  listPage.value = clamped;
  if (clamped !== target) {
    syncPageToQuery(clamped);
  }
}

// Cargar más resultados (siguiente página - para scroll infinito manual)
  async function loadMoreResults() {
  // Esperar a que Fase 2 termine: si aún está cargando, los primeros 500 items
  // no están en searchResults y haríamos un salto de paginación incorrecto.
  if (isLoadingMore.value || !hasMorePages.value || isLoadingMapResults.value) return;

  currentPage.value++;
  await performSearch(false);

  // Tracking de paginación: página 2+ indica usuario muy interesado
  const ciudadTracking = selectedLocations.value.length > 0
    ? selectedLocations.value[0]
    : (currentCity.value || '');
  trackLoadMore({
    ciudad: ciudadTracking,
    pagina: currentPage.value,
    totalResultados: totalResults.value,
  });
}

// Helper para obtener timestamp de un coliving (API serializa Firestore como _seconds)
  function getDateTimestamp(item) {
  const readTs = (ts) => {
    if (!ts) return 0;
    if (typeof ts === 'string') return new Date(ts).getTime();
    const sec = ts.seconds ?? ts._seconds;
    return sec ? sec * 1000 : 0;
  };
  return readTs(item.lastSignificantUpdate)
    || readTs(item.createdAt)
    || readTs(item.lastUpdated)
    || readTs(item.scrapedAt);
}

// Método para ordenar resultados con PRIORIZACIÓN ABSOLUTA de agentes PRO
  function sortSearchResults() {
  const sortBy = filters.value.sortBy;

  // ⭐ PRIORIZACIÓN PRO: Separar colivings PRO de básicos/externos
  const proColivings = searchResults.value.filter(c => c.isProAgent === true);
  const nativeBasics = searchResults.value.filter(c => c.isProAgent !== true && !c.isExternal);
  const scrapedColivings = searchResults.value.filter(c => c.isProAgent !== true && c.isExternal);
  // Resto de sorts: agrupar nativos + scrapeados (comportamiento previo)
  const basicColivings = searchResults.value.filter(c => c.isProAgent !== true);

  // market_relevance: 3 grupos como en el backend (PRO → nativos → scrapeados)
  if (sortBy === 'market_relevance') {
    proColivings.sort(compareMarketRelevance);
    nativeBasics.sort(compareMarketRelevance);
    const { boosted: boostedScrapedMR, regular: regularScrapedMR, demoted: demotedScrapedMR } =
      partitionScrapedBlock(scrapedColivings);
    boostedScrapedMR.sort(compareMarketRelevance);
    regularScrapedMR.sort(compareMarketRelevance);
    demotedScrapedMR.sort(compareMarketRelevance);
    const scrapedMRFinal = finalizeScrapedBlock(
      boostedScrapedMR,
      regularScrapedMR,
      demotedScrapedMR,
      BOOKING_INTEGRATION_ENABLED,
    );
    searchResults.value = [...proColivings, ...nativeBasics, ...scrapedMRFinal];
    return;
  }

  // Función helper para aplicar ordenación a un array
  const applySortToArray = (arr) => {
    // Ordenar por recomendaciones (score más alto primero)
    if (sortBy === 'recommended') {
      arr.sort((a, b) => {
        const scoreA = a.matchData?.score || 0;
        const scoreB = b.matchData?.score || 0;
        
        // Si ambos tienen el mismo score, ordenar por fecha (más reciente primero)
        if (scoreA === scoreB) {
          const getDate = (item) => {
            if (item.lastUpdated?.seconds) return new Date(item.lastUpdated.seconds * 1000);
            if (item.lastUpdated && typeof item.lastUpdated === 'string') return new Date(item.lastUpdated);
            if (item.createdAt?.seconds) return new Date(item.createdAt.seconds * 1000);
            if (item.createdAt && typeof item.createdAt === 'string') return new Date(item.createdAt);
            return new Date(0);
          };
          const dateA = getDate(a);
          const dateB = getDate(b);
          return dateB - dateA;
        }
        
        return scoreB - scoreA;
      });
    }
    // Precio ascendente (menor a mayor)
    else if (sortBy === 'price_asc') {
      arr.sort((a, b) => {
        const priceA = a.priceFrom || a.propertyPrice || 0;
        const priceB = b.priceFrom || b.propertyPrice || 0;
        return priceA - priceB;
      });
    }
    // Precio descendente (mayor a menor)
    else if (sortBy === 'price_desc') {
      arr.sort((a, b) => {
        const priceA = a.priceFrom || a.propertyPrice || 0;
        const priceB = b.priceFrom || b.propertyPrice || 0;
        return priceB - priceA;
      });
    }
    // Fecha más reciente primero
    else if (sortBy === 'date_desc') {
      arr.sort((a, b) => getDateTimestamp(b) - getDateTimestamp(a));
      
      // 🎲 ALEATORIEDAD LIGERA: Mezclar colivings con fechas similares (mismo día)
      const shuffleWindow = 24 * 60 * 60 * 1000; // 24 horas en ms
      
      for (let i = 0; i < arr.length - 1; i++) {
        const currentDate = getDateTimestamp(arr[i]);
        const nextDate = getDateTimestamp(arr[i + 1]);
        
        // Si las fechas están en la misma ventana temporal (mismo día), intercambiar aleatoriamente
        if (Math.abs(currentDate - nextDate) < shuffleWindow && Math.random() > 0.5) {
          [arr[i], arr[i + 1]] = [arr[i + 1], arr[i]];
        }
      }
    }
    // Alfabético A-Z
    else if (sortBy === 'alpha_asc') {
      arr.sort((a, b) => {
        // Usar title o propertyName o externalId como fallback
        const titleA = (a.title || a.propertyName || a.externalId || '').toLowerCase();
        const titleB = (b.title || b.propertyName || b.externalId || '').toLowerCase();
        return titleA.localeCompare(titleB, 'es');
      });
    }
    // Alfabético Z-A
    else if (sortBy === 'alpha_desc') {
      arr.sort((a, b) => {
        // Usar title o propertyName o externalId como fallback
        const titleA = (a.title || a.propertyName || a.externalId || '').toLowerCase();
        const titleB = (b.title || b.propertyName || b.externalId || '').toLowerCase();
        return titleB.localeCompare(titleA, 'es');
      });
    }
    // Habitaciones: menos a más
    else if (sortBy === 'rooms_asc') {
      arr.sort((a, b) => {
        const roomsA = a.totalRooms || a.bedrooms || 0;
        const roomsB = b.totalRooms || b.bedrooms || 0;
        return roomsA - roomsB;
      });
    }
    // Habitaciones: más a menos
    else if (sortBy === 'rooms_desc') {
      arr.sort((a, b) => {
        const roomsA = a.totalRooms || a.bedrooms || 0;
        const roomsB = b.totalRooms || b.bedrooms || 0;
        return roomsB - roomsA;
      });
    }
    // Estancia mínima: menor a mayor
    else if (sortBy === 'stay_asc') {
      arr.sort((a, b) => {
        const stayA = a.minimumStayMonths || 1;
        const stayB = b.minimumStayMonths || 1;
        return stayA - stayB;
      });
    }
    // Disponibilidad (para contratos tradicionales)
    else if (sortBy === 'availability') {
      arr.sort((a, b) => {
        // Primero por disponibilidad
        if (a.assignedApplicant && !b.assignedApplicant) return 1;
        if (!a.assignedApplicant && b.assignedApplicant) return -1;

        // Si ambos tienen el mismo estado de asignación, ordenar por fecha de disponibilidad
        if (!a.assignedApplicant && !b.assignedApplicant) {
          const startDateA = a.startDate ? new Date(a.startDate) : new Date('9999-12-31');
          const startDateB = b.startDate ? new Date(b.startDate) : new Date('9999-12-31');
          return startDateA - startDateB;
        }

        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
    }
  };
  
  // Boost Outsite: separar antes de ordenar el bloque scraped
  const { boosted: boostedScraped, regular: regularScraped, demoted: demotedScraped } =
    partitionScrapedBlock(scrapedColivings);

  // Aplicar ordenación a cada grupo por separado
  applySortToArray(proColivings);
  applySortToArray(basicColivings);
  applySortToArray(boostedScraped);
  applySortToArray(regularScraped);
  applySortToArray(demotedScraped);

  const scrapedFinal = finalizeScrapedBlock(
    boostedScraped,
    regularScraped,
    demotedScraped,
    BOOKING_INTEGRATION_ENABLED,
  );

  // ⭐ Combinar: PRO → nativos → scrapeados (Outsite boost + Homiii booking)
  searchResults.value = [...proColivings, ...nativeBasics, ...scrapedFinal];
}

  return {
    buildSearchParams,
    processApiResults,
    performSearch,
    requestMapResults,
    loadMapResults,
    fetchListResultsUpTo,
    syncPageToQuery,
    resetListPage,
    waitForMapLoad,
    ensureDataForListPage,
    onListPageChange,
    applyInitialUrlPage,
    loadMoreResults,
    sortSearchResults,
    deferredSearch,
    getDateTimestamp,
  };
}
